/**
 * Установка пароля по одноразовой ссылке.
 *
 * Ссылку выдаёт команда `npm run user:create`. Владелец передаёт её лично:
 * почтового канала в первой версии нет.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { setPasswordSchema } from '@project-os/validation';
import { useInvitation, useSetPassword } from '@/api/auth';
import { ApiError } from '@/api/client';
import { ErrorState } from '@/components/common/states';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

export function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const invitation = useInvitation(token);
  const setPassword = useSetPassword();

  const form = useForm({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { token, password: '', passwordConfirmation: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await setPassword.mutateAsync({ ...values, token });
      toast.success('Пароль установлен. Войдите в систему.');
      void navigate('/login', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.fields) {
        for (const [field, message] of Object.entries(error.fields)) {
          form.setError(field as Parameters<typeof form.setError>[0], { message });
        }
      }
    }
  });

  const formError =
    setPassword.error instanceof ApiError && !setPassword.error.fields
      ? setPassword.error
      : undefined;

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <div className="flex justify-end p-3">
        <ThemeToggle />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-24">
        <div className="w-full max-w-sm">
          <h1 className="mb-1 text-center text-xl font-semibold">Установка пароля</h1>

          {invitation.isPending ? (
            <div className="mt-6 space-y-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : invitation.isError ? (
            <ErrorState error={invitation.error} />
          ) : (
            <>
              <p className="mb-6 text-center text-sm text-muted-foreground">
                {invitation.data.name} · {invitation.data.email}
              </p>

              <Form {...form}>
                <form onSubmit={onSubmit} className="space-y-4" noValidate>
                  {formError ? (
                    <Alert variant="destructive">
                      <AlertDescription>{formError.message}</AlertDescription>
                    </Alert>
                  ) : null}

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Новый пароль</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="new-password" autoFocus {...field} />
                        </FormControl>
                        <FormDescription>
                          Не короче 12 символов. Длинная фраза надёжнее короткого набора символов.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="passwordConfirmation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Повторите пароль</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={setPassword.isPending}>
                    {setPassword.isPending ? (
                      <Loader2Icon className="animate-spin" />
                    ) : (
                      <CheckCircle2Icon />
                    )}
                    Сохранить пароль
                  </Button>
                </form>
              </Form>
            </>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">
            <Link to="/login" className="underline underline-offset-4 hover:text-foreground">
              Вернуться ко входу
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
